/**
 * Maps Decode pattern names (returned by Claude in EN or ES) to Library slugs.
 *
 * Claude's `possible_patterns[].name` is freeform text — we normalize it to
 * lowercase and check against a list of synonyms. Returns null when there's
 * no matching Library entry (caller renders no "Learn more" link in that
 * case — graceful fallback).
 */

const ENTRIES = [
  { slug: "gaslighting", needles: [
    "gaslight", "gaslighting", "luz de gas", "gaslighteo",
  ]},
  { slug: "darvo", needles: [
    "darvo",
  ]},
  { slug: "coercive-control", needles: [
    "coercive control", "coercion", "coercive", "control coercitivo", "coerción",
  ]},
  { slug: "trauma-bonding", needles: [
    "trauma bond", "trauma bonding", "vínculo traumático", "vinculo traumatico",
  ]},
  { slug: "isolation", needles: [
    "isolation", "isolating", "aislamiento", "aislar",
  ]},
  { slug: "financial-abuse", needles: [
    "financial abuse", "economic abuse", "abuso financiero", "abuso económico",
  ]},
  { slug: "future-faking", needles: [
    "future fak", "future-fak", "promesas vacías", "futuro falso", "futuro fingido",
  ]},
  { slug: "intermittent-reinforcement", needles: [
    "intermittent reinforcement", "intermittent", "refuerzo intermitente",
  ]},
  { slug: "reactive-abuse", needles: [
    "reactive abuse", "reactive", "abuso reactivo",
  ]},
  { slug: "stalking", needles: [
    "stalking", "stalk", "acecho", "acoso persistente",
  ]},
  { slug: "sexual-coercion", needles: [
    "sexual coercion", "sexual pressure", "coerción sexual",
  ]},
  { slug: "cycle-of-abuse", needles: [
    "cycle of abuse", "tension build", "tension-building",
    "ciclo del abuso", "ciclo de abuso",
    "reconciliation phase", "honeymoon", "luna de miel",
    "calm phase", "fase de calma",
  ]},
  { slug: "power-control-wheel", needles: [
    "power and control", "power & control", "power-control wheel",
    "rueda del poder", "poder y control",
  ]},
];

/**
 * @param {string} name pattern name as returned by Decode (any case, any lang)
 * @returns {string|null} library slug or null when no confident match
 */
export function slugForPattern(name) {
  if (!name || typeof name !== "string") return null;
  const n = name.toLowerCase().trim();
  for (const e of ENTRIES) {
    for (const k of e.needles) {
      if (n.includes(k)) return e.slug;
    }
  }
  return null;
}

/**
 * Counts how many times each canonical pattern slug appears across an array
 * of decode_history rows. Used by the recurring-pattern recognition card.
 * @param {Array} history rows from /api/decode/history
 * @returns {Array<{slug:string,count:number,lastSeen:string}>}
 */
export function aggregatePatterns(history) {
  const map = new Map();
  if (!Array.isArray(history)) return [];
  for (const row of history) {
    const names = Array.isArray(row?.pattern_names) ? row.pattern_names : [];
    const when = row?.created_at || null;
    for (const raw of names) {
      const slug = slugForPattern(raw);
      if (!slug) continue;
      const prev = map.get(slug) || { slug, count: 0, lastSeen: null };
      prev.count += 1;
      if (!prev.lastSeen || (when && when > prev.lastSeen)) prev.lastSeen = when;
      map.set(slug, prev);
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}
